import React, { useState, useEffect } from 'react';
import { Bookmark } from 'lucide-react';
import { api1 } from '../utils/axiosInstance';
import JobsList from '../components/JobsList/JobsList';

const SavedJobs = () => {
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState({
    totalResults: 0,
    hasMore: false,
    limit: 20
  });

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  const fetchSavedJobs = async (page = 0, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await api1.get('/api/saved-jobs', {
        params: {
          page: page,
          limit: 20
        }
      });

      if (response.data.success) {
        const newJobs = response.data.data.jobs;
        
        if (append) {
          setSavedJobs(prevJobs => [...prevJobs, ...newJobs]);
        } else {
          setSavedJobs(newJobs);
        }
        
        setPagination({
          totalResults: response.data.data.totalResults,
          hasMore: response.data.data.hasMore,
          limit: response.data.data.limit
        });
        
        setCurrentPage(page);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to fetch saved jobs');
      }
    } catch (err) {
      setError('Error connecting to the server');
      console.error('Error fetching saved jobs:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleRemoveJob = async (jobId) => {
    try {
      // Immediately remove from UI (optimistic update)
      setSavedJobs(prevJobs => prevJobs.filter(job => job.jobId !== jobId));
      setPagination(prev => ({ ...prev, totalResults: prev.totalResults - 1 }));
      
      // Then make the API call
      const response = await api1.delete(`/api/saved-jobs/${jobId}`);
      
      // If API call fails, revert the UI state
      if (!response.data.success) {
        // Revert the optimistic update by refetching the data
        fetchSavedJobs(currentPage);
        console.error('Failed to remove job:', response.data.message);
      }
    } catch (error) {
      // If API call fails, revert the optimistic update by refetching the data
      fetchSavedJobs(currentPage);
      console.error('Error removing job:', error);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-400 p-8 w-full">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Bookmark className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">My Saved Jobs</h2>
            <p className="text-gray-600 text-base">Your bookmarked job listings</p>
          </div>
        </div>
      </div>

      {/* Jobs List Component */}
      <JobsList
        jobs={savedJobs}
        savedJobs={{}}
        loading={loading}
        loadingMore={loadingMore}
        error={error}
        currentPage={currentPage}
        pagination={pagination}
        showPagination={savedJobs.length > 0 && pagination.totalResults > 20}
        showRemoveButton={true}
        emptyStateMessage="No jobs saved yet."
        onRemoveJob={handleRemoveJob}
        onPreviousPage={() => fetchSavedJobs(currentPage - 1)}
        onNextPage={() => fetchSavedJobs(currentPage + 1)}
        onRetry={fetchSavedJobs}
      />
    </div>
  );
};

export default SavedJobs;